import { NavMenu } from '@shopify/app-bridge-react';
import { isMobile, isShopifyEmbedded } from '@shopify/app-bridge-utils';
import { AppProvider as PolarisProvider } from '@shopify/polaris';
import enTranslations from '@shopify/polaris/locales/en.json';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
// import { isMobile,isShopifyEmbedded } from '@shopify/app-bridge-core';
import RouterLink from '@/shared/links/RouterLink';
import { GrowthBook, GrowthBookProvider } from '@growthbook/growthbook-react';
import '@shopify/polaris/build/esm/styles.css';
import 'assets/sass/app.scss';
import { PATHS } from 'config/constants';
import { useChatwoot } from 'hooks/useChatwoot';
import { useShopInfo } from 'hooks/useShopInfo';

import { useShopInfoCrossTabSync } from 'hooks/useShopInfoCrossTabSync';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Link } from 'react-router';
import { setApiAppBridge } from 'rest/dist/utils/fetchInstance';
import { getPlansAction } from 'states/actions/plans';
import { getSettingAction } from 'states/actions/setting';
import { initAppBridge } from 'utils/shopifyNav';
import ScrollToTop from '@/shared/ScrollToTop';
import Router from './Router';
import { api } from './api';
import { getShopInfoAction } from './states/actions/shop';
import { loadScript } from './utils/loadScript';

const withEmbeddedParams = (path) => {
  const sp = new URLSearchParams(window.location.search);
  const host = sp.get('host');
  const shop = sp.get('shop');
  const [p, q] = String(path).split('?');
  const next = new URLSearchParams(q || '');
  if (host && !next.get('host')) next.set('host', host);
  if (shop && !next.get('shop')) next.set('shop', shop);
  const qs = next.toString();
  return qs ? `${p}?${qs}` : p;
};

const growthbook = new GrowthBook({
  apiHost: 'https://cdn.growthbook.io',
  clientKey: 'sdk-XMfFOBpXD7Nvb6vF',
  subscribeToChanges: true,
  trackingCallback: (experiment, result) => {
    /* console.log('Viewed Experiment', {
      experimentId: experiment.key,
      variationId: result.key,
    }); */
  },
});

const App = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const shopInfo = useShopInfo();
  useShopInfoCrossTabSync();

  useEffect(() => {
    growthbook.loadFeatures();
  }, []);

  const [isLoggedInAsUser, setIsLoggedInAsUser] = useState(false);
  useChatwoot(!isLoggedInAsUser && Boolean(shopInfo?.data?.shopify_domain));
  useEffect(() => {
    // window.top !== window.self
    if (isMobile() || isShopifyEmbedded()) {
      const queryParams = new URLSearchParams(window.location.search);
      const host = queryParams.get('host');
      const shopDomain = queryParams.get('shop');

      const boot = () => {
        initAppBridge(host);
        setApiAppBridge({ host, shopDomain });
        getShopInfoAction(dispatch)(host);
      };

      // In the admin iframe, index.html's document.write already loaded the
      // App Bridge CDN script render-blocking and first-in-head (Shopify's
      // requirement, and what makes Core Web Vitals reporting count), so
      // there's nothing to wait for. That injection is deliberately gated on
      // `window.top !== window.self` — App Bridge v4 auto-redirects any
      // TOP-LEVEL load into the admin, which used to strand localhost dev and
      // /support/login on a blank redirecting page. The one embedded case
      // that IS top-level (Shopify mobile app webview → isMobile()) therefore
      // still needs a runtime injection; index.html's <link rel="preload">
      // keeps that fetch warm so this resolves from cache.
      if (window.shopify) {
        boot();
      } else {
        loadScript('https://cdn.shopify.com/shopifycloud/app-bridge.js')
          .then(boot)
          .catch((error) => {
            console.error('Error loading Shopify App Bridge script:', error);
          });
      }
    } else {
      try {
        // let isInstalled = false;
        const location = window.location;
        const queryParams = new URLSearchParams(window.location.search);
        // const host = queryParams.get('host');
        const shopDomain = queryParams.get('shop');
        if (shopDomain) {
          // Managed install + token exchange: there is no app-side OAuth route
          // anymore. Always open the app embedded in admin; Shopify runs the
          // managed install if the shop hasn't installed yet, and token exchange
          // then authenticates the embedded session.
          const shopnewdomain = shopDomain.replace('.myshopify.com', '');
          // Use the per-environment app handle (dev/stage/prod each set their
          // own REACT_APP_SHOPIFY_APP_SLUG) — hardcoding the prod handle sent
          // dev/stage stores to the wrong app's "not installed" page.
          location.href =
            'https://admin.shopify.com/store/' +
            shopnewdomain +
            '/apps/' +
            process.env.REACT_APP_SHOPIFY_APP_SLUG;
        } else {
          setIsLoggedInAsUser(true);
          api.setAppBridge('', '', true);
          try {
            const location = window.location;

            const queryParams = new URLSearchParams(window.location.search);
          } catch (error) {}
          const token = sessionStorage.getItem('loggedInAsUserToken');
          const isLoginPage = window.location.pathname.includes('/support/login');
          if (!isLoginPage) {
            if (token) {
              getShopInfoAction(dispatch)();
            } else {
              window.open(window.location.origin + PATHS.LOGIN, '_self');
            }
          } else {
            getShopInfoAction(dispatch)('', true);
          }
        }
      } catch (err) {
        console.error(err);
      }
    }
  }, [dispatch]);

  useEffect(() => {
    getPlansAction(dispatch)();
    getSettingAction(dispatch)();
  }, [shopInfo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isShopifyEmbedded() && shopInfo?.data?.shopify_domain) {
      const created_at = new Date(shopInfo?.data?.created_at).getTime();
      growthbook.setAttributes({
        name: shopInfo?.data?.name,
        shopify_domain: shopInfo?.data?.shopify_domain,
        created_at,
      });
    }
  }, [shopInfo]);

  // NOTE: there is deliberately NO `if (loadingShopInfo || loadingPermissions)
  // return <WholePageLoading />` here any more. That early return made the whole
  // app — Polaris provider, nav, page shell, everything — wait on two network
  // round-trips, so LCP was literally "whenever the slowest boot API answered",
  // and the spinner→content swap was a guaranteed layout shift on every load.
  //
  // The tree renders immediately now and fills in as data lands. Every live
  // consumer of shopInfo/permissions already optional-chains through the empty
  // initial state ({} / undefined, see states/reducers/shop.js), and the pages
  // that fetch their own data carry their own skeletons. Anything added later
  // that reads shop data must guard the same way rather than reinstating a
  // global gate. See CLAUDE.md "Core Web Vitals budget".

  return (
    <>
      <GrowthBookProvider growthbook={growthbook}>
        <PolarisProvider i18n={enTranslations} theme={'light'} linkComponent={RouterLink}>
          {isLoggedInAsUser ? (
            <BrowserRouter>
              <ScrollToTop />
              <Router />
            </BrowserRouter>
          ) : (
            <BrowserRouter>
              <ScrollToTop />
              <AppContent host={shopInfo?.host} />
              <NavMenu>
                {[
                  {
                    label: t('rules'),
                    destination: PATHS.RULES,
                  },
                  {
                    label: t('design.navLabel'),
                    destination: PATHS.DESIGN,
                  },
                  {
                    label: t('automations.navLabel'),
                    destination: PATHS.AUTOMATIONS,
                  },
                  {
                    // No nav entries for Pro features C/D/E: Alerts and
                    // Integrations live inside Settings, and "Blocked cart
                    // value" reports live on the Dashboard.
                    label: t('plans'),
                    destination: PATHS.PRICING,
                  },
                  {
                    label: t('settings'),
                    destination: PATHS.SETTING,
                  },
                  {
                    label: t('su.pageTitle'),
                    destination: PATHS.SAFE_UNINSTALL,
                  },
                  {
                    label: t('d.dashboardFooterLink.helpCenter'),
                    destination: PATHS.HELP,
                  },
                ].map((item, index) => (
                  <Link to={withEmbeddedParams(item.destination)} rel={item.label} key={index}>
                    {item.label}
                  </Link>
                ))}
              </NavMenu>

              <Router />
            </BrowserRouter>
          )}
        </PolarisProvider>
      </GrowthBookProvider>
    </>
  );
};

export default App;

function AppContent({ host }) {
  React.useEffect(() => {
    if (!host) return;

    const app = initAppBridge(host);
    if (app) {
      app.getState().then((state) => {
        // console.log('AppBridge state:', state)
      });
    }
  }, [host]);

  return null;
}
